import axios, { AxiosResponse, HttpStatusCode } from 'axios';
import httpClient from '@/src/lib/api/http/client';
import {
  IOutletDetailProduct,
  IOutletDetailService,
} from '@/src/models/outlet-detail';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import { mapBookServiceToBookServiceRequest } from '../data/mapper';
import { TBookServiceSlot } from '../data/schemas/book-service-slot';
import {
  SCreateBookServiceSlotResponse,
  TCreateBookServiceSlotResponse,
} from '../data/schemas/response';
import {
  BookServiceFailedError,
  GetOutletProductsFailedError,
  GetOutletServicesFailedError,
  ServiceSlotExistsError,
  ServiceSlotNotFoundError,
} from '../errors';

interface IGetOutletDetailParams {
  offset: number;
  size: number;
  outlet_id: number;
  search?: string;
}

interface IGetOutletProductsParams {
  offset: number;
  size: number;
  outlet_id: number;
  latitude: number | undefined;
  longitude: number | undefined;
  search?: string;
}

interface ICreateBookSlotParams {
  input: TBookServiceSlot;
  serviceSlotId: number;
  method: string | null;
}

export const outletService = {
  getOutletServices: async ({
    offset,
    size,
    outlet_id,
    search,
  }: IGetOutletDetailParams) => {
    const params = {
      offset,
      size,
      outlet_id,
      search,
      ignore_test_account: true,
    };

    try {
      const response: AxiosResponse<IOutletDetailService> =
        await httpClient.healthcareIntelligence.get(
          '/marketplace/get-marketplace-service',
          { params }
        );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new GetOutletServicesFailedError();
      }

      throw error;
    }
  },

  getOutletProducts: async ({
    offset,
    size,
    outlet_id,
    latitude,
    longitude,
    search,
  }: IGetOutletProductsParams) => {
    const params = {
      offset,
      size,
      outlet_id,
      latitude,
      longitude,
      search,
      ignore_test_account: true,
    };

    try {
      const response: AxiosResponse<IOutletDetailProduct> =
        await httpClient.healthcareIntelligence.get(
          '/marketplace/get-marketplace-product',
          { params }
        );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new GetOutletProductsFailedError();
      }

      throw error;
    }
  },

  createBookServiceSlot: async ({
    input,
    serviceSlotId,
    method,
  }: ICreateBookSlotParams) => {
    const request = mapBookServiceToBookServiceRequest(
      input,
      serviceSlotId,
      method
    );

    try {
      const response: AxiosResponse<TCreateBookServiceSlotResponse> =
        await httpClient.marketplaceCore.post(
          '/service/post-book-service',
          request
        );
      const responseData = safeParseZodObject<TCreateBookServiceSlotResponse>(
        SCreateBookServiceSlotResponse,
        response.data
      );
      return responseData.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        switch (error.response?.data.error.code) {
          case HttpStatusCode.NotFound:
            throw new ServiceSlotNotFoundError();
          case HttpStatusCode.Conflict:
            throw new ServiceSlotExistsError();
        }

        throw new BookServiceFailedError();
      }

      throw error;
    }
  },
};
